import { Component, OnDestroy, OnInit } from "@angular/core";
import { forkJoin, Subject, timer, EMPTY } from "rxjs";
import { switchMap, tap, takeUntil, filter } from "rxjs/operators";
import { OverviewStateService } from "src/state/overview/service";
import { CommonStateService } from "src/state";
import { Overview, SimulationOverview, MissingPartDetails } from "src/state/overview/state-models/overview-data";

export interface PanelStat {
    label: string;
    value: number;
    colorClass: "stat-green" | "stat-orange" | "stat-red";
    dotClass: "dot-green" | "dot-orange" | "dot-red";
}

export interface DashboardPanel {
    title: string;
    iconPath: string;
    stats: PanelStat[];
}

export interface DonutSegment {
    color: string;
    dasharray: string;
    dashoffset: number;
    label: string;
    value: number;
}

export interface AllocationOverview {
    segments: DonutSegment[];
    total: number;
    centerLabel: string;
}

export interface AllocationUnit {
    name: string;
    iconPath: string;
    allocated: number;
    produce: number;
    errors: number;
    status: "Allocated" | "Pending";
}

export interface AllocationByUnit {
    title: string;
    rows: AllocationUnit[];
}


@Component({
    selector: "cal-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.css"],
})
export class DashboardComponent implements OnInit, OnDestroy {
    panels: DashboardPanel[] = [
        {
            title: "Component",
            iconPath: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
            stats: [
                {
                    label: "Allocated",
                    value: 0,
                    colorClass: "stat-green",
                    dotClass: "dot-green",
                },
                {
                    label: "Produce",
                    value: 0,
                    colorClass: "stat-orange",
                    dotClass: "dot-orange",
                },
                {
                    label: "Errors",
                    value: 0,
                    colorClass: "stat-red",
                    dotClass: "dot-red",
                },
            ],
        },
        {
            title: "Final Assembly",
            iconPath:
                "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z",
            stats: [
                {
                    label: "Allocated",
                    value: 0,
                    colorClass: "stat-green",
                    dotClass: "dot-green",
                },
                {
                    label: "Produce",
                    value: 0,
                    colorClass: "stat-orange",
                    dotClass: "dot-orange",
                },
                {
                    label: "Errors",
                    value: 0,
                    colorClass: "stat-red",
                    dotClass: "dot-red",
                },
            ],
        },
        {
            title: "Simulation",
            iconPath: "M18 20V10M12 20V4M6 20v-6",
            stats: [
                {
                    label: "Total Simulation",
                    value: 0,
                    colorClass: "stat-green",
                    dotClass: "dot-green",
                },
                {
                    label: "SEU Simulation",
                    value: 0,
                    colorClass: "stat-orange",
                    dotClass: "dot-orange",
                },
                {
                    label: "SLA Simulation",
                    value: 0,
                    colorClass: "stat-red",
                    dotClass: "dot-red",
                },
            ],
        },
    ];

    allocationOverviews: AllocationOverview[] = [];

    allocationByUnits: AllocationByUnit[] = [];

    missingPartMarket1: MissingPartDetails | null = null;
    missingPartMarket2: MissingPartDetails | null = null;

    loadingMain = true;
    loadingSimDetails = true;

    private readonly DONUT_CIRC = 2 * Math.PI * 35;
    private readonly SEG_COLORS = ["#28a745", "#fd7e14", "#dc3545"];
    private readonly REFRESH_INTERVAL = 120000; // 2 minutes
    private readonly destroy$ = new Subject<void>();
    private tabVisible = true;

    constructor(
        private stateService: OverviewStateService,
        private commonService: CommonStateService,
    ) {}

    ngOnInit(): void {
        this.loadDashboardData();
        this.startAutoRefresh();
        this.listenToVisibility();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private listenToVisibility(): void {
        document.addEventListener("visibilitychange", () => {
            this.tabVisible = !document.hidden;
        });
    }

    private startAutoRefresh(): void {
        timer(this.REFRESH_INTERVAL, this.REFRESH_INTERVAL).pipe(
            takeUntil(this.destroy$),
            filter(() => this.tabVisible),
            switchMap(() => {
                return forkJoin({
                    components: this.stateService.GetByComponents(),
                    finalAssembly: this.stateService.GetByFinalAssembly(),
                    simulation: this.stateService.GetSimulation(),
                });
            }),
        ).subscribe(({ components, finalAssembly, simulation }) => {
            this.updatePanelStats(0, components);
            this.updatePanelStats(1, finalAssembly);
            this.updateSimulationPanel(simulation);

            this.allocationOverviews = [
                this.buildOverview(this.panels[0]),
                this.buildOverview(this.panels[1]),
                this.buildSimulationOverview(simulation),
            ];

            this.allocationByUnits = [
                {
                    title: "Component Allocation",
                    rows: this.mapOverviewToRows(components),
                },
                {
                    title: "Final Assembly Allocation",
                    rows: this.mapOverviewToRows(finalAssembly),
                },
            ];
        });
    }

    private loadDashboardData(): void {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const fromDate = oneMonthAgo.toISOString().split("T")[0];
        const toDate = today.toISOString().split("T")[0];

        forkJoin({
            components: this.stateService.GetByComponents(),
            finalAssembly: this.stateService.GetByFinalAssembly(),
            simulation: this.stateService.GetSimulation(),
        }).subscribe(({ components, finalAssembly, simulation }) => {
            this.updatePanelStats(0, components);
            this.updatePanelStats(1, finalAssembly);
            this.updateSimulationPanel(simulation);

            this.allocationOverviews = [
                this.buildOverview(this.panels[0]),
                this.buildOverview(this.panels[1]),
                this.buildSimulationOverview(simulation),
            ];

            this.allocationByUnits = [
                {
                    title: "Component Allocation",
                    rows: this.mapOverviewToRows(components),
                },
                {
                    title: "Final Assembly Allocation",
                    rows: this.mapOverviewToRows(finalAssembly),
                },
            ];

            this.loadingMain = false;
        });

        this.stateService.GetSimulationMissingPart(fromDate, toDate, 1, 2).pipe(
            tap((data) => {
                this.missingPartMarket1 = data;
            }),
            switchMap(() => this.stateService.GetSimulationMissingPart(fromDate, toDate, 2, 2))
        ).subscribe((data) => {
            this.missingPartMarket2 = data;
            this.loadingSimDetails = false;
        });
    }

    private refreshSimDetails(): void {
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const fromDate = oneMonthAgo.toISOString().split("T")[0];
        const toDate = today.toISOString().split("T")[0];

        this.stateService.GetSimulationMissingPart(fromDate, toDate, 1, 2).pipe(
            tap((data) => {
                this.missingPartMarket1 = data;
            }),
            switchMap(() => this.stateService.GetSimulationMissingPart(fromDate, toDate, 2, 2))
        ).subscribe((data) => {
            this.missingPartMarket2 = data;
        });
    }

    private updateSimulationPanel(data: SimulationOverview): void {
        if (data) {
            this.panels[2].stats[0].value = data.totalSimulation;
            this.panels[2].stats[1].value = data.seuSimulation;
            this.panels[2].stats[2].value = data.slaSimulation;
        }
    }

    private updatePanelStats(panelIndex: number, data: Overview[]): void {
        const allocated = data.reduce((sum, item) => sum + item.allocatedCount, 0);
        const produce = data.reduce((sum, item) => sum + item.produceCount, 0);
        const errors = data.reduce((sum, item) => sum + item.produceCountError, 0);

        this.panels[panelIndex].stats[0].value = allocated;
        this.panels[panelIndex].stats[1].value = produce;
        this.panels[panelIndex].stats[2].value = errors;
    }

    private mapOverviewToRows(data: Overview[]): AllocationUnit[] {
        const defaultIcon = "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5";
        return data.map((item) => ({
            name: item.name,
            iconPath: defaultIcon,
            allocated: item.allocatedCount,
            produce: item.produceCount,
            errors: item.produceCountError,
            status: this.calcStatus(item),
        }));
    }

    private calcStatus(item: Overview): "Allocated" | "Pending" {
        if (item.allocatedCount === item.totalCount) {
            return "Allocated";
        }
        if (item.produceCount === item.totalCount) {
            return "Pending";
        }
        if (item.allocatedCount + item.produceCount + item.produceCountError === item.totalCount) {
            return "Pending";
        }
        return "Pending";
    }

    private buildOverview(panel: DashboardPanel): AllocationOverview {
        const circ = this.DONUT_CIRC;
        const total = panel.stats.reduce((s, st) => s + st.value, 0);
        const minArc = circ * 0.02; // minimum 2% arc for visibility

        // Calculate raw lengths
        const rawLengths = panel.stats.map((stat) =>
            total > 0 ? (stat.value / total) * circ : 0
        );

        // Apply minimum arc and adjust others proportionally
        const lengths = rawLengths.map((len) =>
            len > 0 && len < minArc ? minArc : len
        );
        const lengthSum = lengths.reduce((s, l) => s + l, 0);
        const scale = lengthSum > 0 ? circ / lengthSum : 1;
        const adjusted = lengths.map((l) => l * scale);

        let cum = 0;
        const segments: DonutSegment[] = panel.stats.map((stat, i) => {
            const seg: DonutSegment = {
                color: this.SEG_COLORS[i],
                dasharray: `${adjusted[i]} ${circ - adjusted[i]}`,
                dashoffset: -cum,
                label: stat.label,
                value: stat.value,
            };
            cum += adjusted[i];
            return seg;
        });
        return {
            segments,
            total,
            centerLabel: panel.title,
        };
    }

    private buildSimulationOverview(data: SimulationOverview): AllocationOverview {
        const circ = this.DONUT_CIRC;
        const seuColor = "#fd7e14";
        const slaColor = "#dc3545";
        const simData = [
            { value: data?.seuSimulation || 0, color: seuColor, label: "SEU Simulation" },
            { value: data?.slaSimulation || 0, color: slaColor, label: "SLA Simulation" },
        ];
        const total = simData.reduce((s, d) => s + d.value, 0);
        let cum = 0;

        const segments: DonutSegment[] = simData.map((item) => {
            const len = total > 0 ? (item.value / total) * circ : 0;
            const seg: DonutSegment = {
                color: item.color,
                dasharray: `${len} ${circ - len}`,
                dashoffset: -cum,
                label: item.label,
                value: item.value,
            };
            cum += len;
            return seg;
        });

        return {
            segments,
            total: data?.totalSimulation || 0,
            centerLabel: "Simulation",
        };
    }

    formatValue(value: number): string {
        return value.toLocaleString("en-US");
    }
}
